import { useDraggable } from "@dnd-kit/core";
import { type FC, type PointerEventHandler, useId } from "react";
import { Heading } from "@/components/Atoms/Heading/Heading";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { useAdminFormEditorPaths } from "../../hooks/use-admin-form-editor-paths";
import { PALETTE_ITEMS } from "../../lib/palette-metadata";
import type { PaletteFieldType } from "../../types";
import { translatePaletteFieldTypeLabel } from "./form-builder-palette-label";

export const PALETTE_PREFIX = "palette::";

/**
 * Right-hand palette: draggable field types plus click-to-add on each row.
 * The panel scrolls as one column, with a fade over the last rows.
 */
export function FieldPalette({
	onAddClick,
	titleId: titleIdProp,
}: {
	onAddClick: (fieldType: PaletteFieldType) => void;
	/** When the palette sits in a landmark, pass the parent `useId()` so `aria-labelledby` matches. */
	titleId?: string;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const generatedTitleId = useId();
	const titleId = titleIdProp ?? generatedTitleId;

	return (
		<div className="relative flex min-h-0 flex-1 flex-col">
			<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-1">
				<header className="flex flex-col gap-1">
					<Heading id={titleId} level="h4">
						{t(paths.formEditor.palette.title)}
					</Heading>
					<p className="text-sm font-semibold leading-5 text-secondary">
						{t(paths.formEditor.palette.subtitle)}
					</p>
				</header>
				<div className="flex flex-col gap-2">
					{PALETTE_ITEMS.map(({ type, Icon }) => (
						<PaletteRow
							key={type}
							fieldType={type}
							title={translatePaletteFieldTypeLabel(t, paths, type)}
							Icon={Icon}
							onAdd={() => {
								onAddClick(type);
							}}
							paletteRowKey={paths.formEditor.a11y.paletteRow}
						/>
					))}
					<div className="h-10 shrink-0" aria-hidden />
				</div>
			</div>
			<div
				aria-hidden
				className="pointer-events-none absolute inset-x-0 bottom-0 h-[66px] bg-gradient-to-b from-white/10 to-white"
			/>
		</div>
	);
}

function PaletteRow({
	fieldType,
	title,
	Icon,
	onAdd,
	paletteRowKey,
}: {
	fieldType: PaletteFieldType;
	title: string;
	Icon: FC<{ className?: string }>;
	onAdd: () => void;
	paletteRowKey: string;
}) {
	const { t } = useAppTranslation();
	const { attributes, listeners, setNodeRef, isDragging } =
		useDraggablePalette(fieldType);
	// The row's Dropdown.Item stops pointer propagation, so dnd-kit's pointer-down
	// must run in the capture phase to start a drag from anywhere on the row.
	const { onPointerDown, ...restListeners } = listeners ?? {};

	return (
		<div
			ref={setNodeRef}
			{...restListeners}
			onPointerDownCapture={
				onPointerDown as PointerEventHandler<HTMLDivElement> | undefined
			}
			{...attributes}
			className={cx(
				"cursor-grab active:cursor-grabbing",
				isDragging && "opacity-50",
			)}
		>
			<Dropdown.Menu
				aria-label={title}
				selectionMode="none"
				className="w-full"
			>
				<Dropdown.Item
					id={fieldType}
					label={title}
					unstyled
					onAction={onAdd}
					aria-label={t(paletteRowKey as never, {
						label: title,
					})}
					data-testid={`form-builder-palette-${fieldType}`}
					className="w-full cursor-pointer rounded-md outline-hidden"
				>
					<span className="flex h-[38px] w-full items-center gap-2 rounded-md border border-gray-300 bg-primary px-3 hover:bg-primary_hover">
						<span aria-hidden className="inline-flex">
							<Icon className="size-5 shrink-0 text-gray-950" />
						</span>
						<span className="truncate text-sm font-semibold text-secondary">
							{title}
						</span>
					</span>
				</Dropdown.Item>
			</Dropdown.Menu>
		</div>
	);
}

function useDraggablePalette(fieldType: PaletteFieldType) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `${PALETTE_PREFIX}${fieldType}`,
		data: { type: "FIELD_TYPE", fieldType },
	});
	return { attributes, listeners, setNodeRef, isDragging };
}
